import React, { useState } from "react";
import { Stack } from "@mui/material";
import AuthInput from "../../components/Input/AuthInput";
import AuthButton from "../../components/Button/AuthButton";
import VerticalGap from "../../components/Gap/VerticalGap";
import AuthCheckLabel from "../../components/CheckBox/AuthCheckLabel";
import LabelButton from "../../components/Button/LabelButton";
import { LoginUserTy } from "../../config/types";
import { AuthApi } from "../../services/auth";
import useAppNavigation from "../../hooks/useAppNavigation";
import useFormUpdateHelper from "../../hooks/useInputHelper";
import { defaultValue, FormField, transformForm } from "../../utils/formSchema";
import { useFieldHasError } from "../../hooks/useFieldHasError";
import { isFormFilled } from "../../utils/validators";
import { useKeyDownOnPress } from "../../hooks/useKeyDownOnPress";
import { alertDialogStore } from "../../store/dialogs";

interface LoginProps {
  onMainBtnClick: () => void;
  onLabelButtonClick: () => void;
  pageProps: any;
}

interface LoginForm extends FormField<LoginUserTy> {
  rememberMe: boolean;
  submitAttempt: boolean;
}

const INITAIL_VALUE: LoginForm = {
  username: defaultValue,
  password: defaultValue,
  rememberMe: false,
  submitAttempt: false,
};

const Login: React.FC<LoginProps> = ({ pageProps }) => {
  const [form, upDateForm] = useState<LoginForm>(INITAIL_VALUE);
  const [isProcessing, setProcess] = useState<boolean>(false);
  const fieldHasError = useFieldHasError<LoginForm>(form);
  const handleFormUpdate = useFormUpdateHelper<LoginForm>(upDateForm);
  const appNavigation = useAppNavigation();

  const handleChecked = (_event: React.SyntheticEvent, checked: boolean) => {
    upDateForm((prev) => ({
      ...prev,
      rememberMe: checked,
    }));
  };
  const handleSubmit = async () => {
    upDateForm((prev) => ({
      ...prev,
      submitAttempt: true,
    }));

    const isValid = isFormFilled<LoginForm>(form);
    if (isValid) {
      setProcess(true);
      const rawform = transformForm<LoginUserTy>(form);

      try {
        const res = await AuthApi.loginUser({
          username: rawform.username as string,
          password: rawform.password as string,
        });
        if (res.isSuccess) {
          appNavigation({path:"/",isSibling: false});
        } else {
          const is_inactive = res.data?.is_active === false;
          alertDialogStore.open({
            title: "Login",
            subtitle: res.message ?? "Login failed. Please try again.",
            onLeftBtnPress: () => {},
            onRightBtnPress: () => {
              is_inactive &&
                appNavigation({path:"create-password",options: {
                  state: { email: res.data?.email },
                }});
            },
            leftBtnTxt: "Canel",
            rightBtnTxt: is_inactive ? "Active Account" : "Okay",
            isSuccess: false,
            isFullScreen: true,
          });
        }
      } catch (error) {
        alert("An error occurred. Please try again.");
      } finally {
        setProcess(false);
      }
    }
  };
  useKeyDownOnPress(handleSubmit);
  return (
    <Stack>
      <AuthInput
        value={form.username.value}
        type="email"
        onChange={handleFormUpdate("username", "email")}
        error={fieldHasError("username")}
        errorMessage={form.username.errorMessage}
      />
      <VerticalGap height={20} />

      <AuthInput
        value={form.password.value}
        type="password"
        onChange={handleFormUpdate("password")}
        error={fieldHasError("password")}
        errorMessage={form.password.errorMessage}
      />
      <VerticalGap height={20} />

      <AuthCheckLabel
        value={form.rememberMe}
        label="Remember me"
        onChange={handleChecked}
      />
      <VerticalGap height={20} />

      <Stack direction={"row"} gap={3} height={40}>
        <AuthButton
          title={pageProps?.BtnText}
          size={pageProps?.BtnType}
          showIcon={pageProps?.BtnIcon}
          onPress={handleSubmit}
          loading={isProcessing}
        />
        <LabelButton
          onPress={() => appNavigation({path:"forgot-password"})}
          label={pageProps.infoLabelIconText}
          direction={pageProps.labelDirection}
        />
      </Stack>
    </Stack>
  );
};

export default Login;
